import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DateInput } from "@/components/DateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FilePlus2, Loader2 } from "lucide-react";

const today = () => new Date().toLocaleDateString("sv-SE");
const plusDays = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toLocaleDateString("sv-SE"); };
const EMPTY = { car_id: "", pickup_date: today(), return_date: plusDays(today(), 1), first_name: "", last_name: "", phone: "", email: "" };

export const NewContractButton = () => {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [cars, setCars] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/admin/cars").then((r) => setCars(r.data)).catch(() => {}); }, [open]);
  const set = (k) => (e) => setF((p) => {
    const next = { ...p, [k]: e.target ? e.target.value : e };
    if (k === "pickup_date" && next.return_date <= next.pickup_date) next.return_date = plusDays(next.pickup_date, 1);
    return next;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.car_id) { toast.error("Scegli l'auto"); return; }
    if (!f.pickup_date || !f.return_date) { toast.error("Data non valida: usa gg/mm/aaaa"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/bookings/walk-in", { ...f, email: f.email.trim() || null });
      toast.success(`Noleggio ${data.code} creato, date bloccate in calendario`);
      setOpen(false); setF(EMPTY);
      nav(`/admin/contratto/${data.id}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-[#E63946] hover:bg-[#D62828] text-white mb-5" data-testid="new-contract-btn">
          <FilePlus2 className="w-4 h-4 mr-2" />Nuovo contratto (cliente in sede)
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg bg-[#12141A] border-white/10" data-testid="new-contract-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">Nuovo contratto</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Auto</Label>
            <Select value={f.car_id} onValueChange={set("car_id")}>
              <SelectTrigger className="bg-[#0B0C10] border-white/10" data-testid="new-contract-car"><SelectValue placeholder="Scegli l'auto" /></SelectTrigger>
              <SelectContent>{cars.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`new-contract-car-${c.id}`}>{c.brand} {c.model}{c.plate ? ` · ${c.plate}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Ritiro</Label><DateInput value={f.pickup_date} onChange={set("pickup_date")} required className="bg-[#0B0C10] border-white/10" data-testid="new-contract-pickup" /></div>
            <div className="grid gap-1.5"><Label>Riconsegna</Label><DateInput value={f.return_date} onChange={set("return_date")} required className="bg-[#0B0C10] border-white/10" data-testid="new-contract-return" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Nome</Label><Input value={f.first_name} onChange={set("first_name")} required className="bg-[#0B0C10] border-white/10" data-testid="new-contract-first-name" /></div>
            <div className="grid gap-1.5"><Label>Cognome</Label><Input value={f.last_name} onChange={set("last_name")} required className="bg-[#0B0C10] border-white/10" data-testid="new-contract-last-name" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Telefono</Label><Input value={f.phone} onChange={set("phone")} className="bg-[#0B0C10] border-white/10" data-testid="new-contract-phone" /></div>
            <div className="grid gap-1.5"><Label>Email (facoltativa)</Label><Input type="email" value={f.email} onChange={set("email")} className="bg-[#0B0C10] border-white/10" data-testid="new-contract-email" /></div>
          </div>
          <p className="text-xs text-muted-foreground">Pagamento in sede, prenotazione confermata. Gli altri dati (codice fiscale, patente…) li compili nel contratto, anche leggendoli dalle foto dei documenti.</p>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-full bg-[#E63946] hover:bg-[#D62828] text-white" data-testid="new-contract-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}Crea e apri contratto
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
